import controllers from "@/controllers";
import DateTimeHandlerService from "@/services/common/date-time/DateTimeHandlerService";

/**
 * Factory para criar um objeto vazio dos dados do gráfico de análise comparativa.
 * @returns State do vuex para os dados do gráfico de análise comparativa
 */
const FactoryEmptyComparativeScatterChartState = function()
{
	return {
		alarm:     0,
		endDate:   "",
		startDate: "",
		trends:    [],
		trip:      0,
	};
};

/**
 * Criar o objeto modelo de para fazer chamada de domínio personalizado.
 * @param {object} dateTime - Objeto contendo data/hora de início e fim.
 * @param {object} domainSettings - Objeto com as configurações de domínio personalizado.
 * @returns {object} Modelo para chamada de domínio personalizado.
 */
const FactoryScatterChartModel = function(dateTime, domainSettings)
{
	return {
		domainSettings: domainSettings,
		startDateTime:  DateTimeHandlerService.ToUTCDateTime(`${dateTime.startDate} ${dateTime.startTime}`),
		endDateTime:    DateTimeHandlerService.ToUTCDateTime(`${dateTime.endDate} ${dateTime.endTime}`),
	};
};

/**
 * Cria o objeto um modelo de configuração de domínio personalizado.
 * @param {string} assetId - O ID do ativo.
 * @param {string} trendSignalSettingId - O ID da configuração de sinal de tendência.
 * @param {string} frequencyTrendSettingId - O ID da configuração de componente de frequência.
 * @param {string} customTrendSignalSettingId - O ID da configuração de sinal de tendência personalizada.
 * @returns {Object} Modelo de configuração de domínio personalizado.
 */
const FactoryScatterSettingModel = function(
	assetId,
	trendSignalSettingId,
	frequencyTrendSettingId,
	customTrendSignalSettingId
)
{
	return {
		generatingUnitMonitoringElementId: assetId,
		trendSignalSettingIds:             trendSignalSettingId,
		frequencySettingIds:               frequencyTrendSettingId,
		customTrendSignalSettingId:        customTrendSignalSettingId,
	};
};

/**
 * Criar o objeto modelo de para fazer chamada de domínio personalizado com estratificações.
 * @param {object} dateTime - Objeto contendo data/hora de início e fim.
 * @param {object} stratificationSettings - Objecto com as configurações de estratificação.
 * @returns Modelo para chamada de domínio personalizado com estratificações.
 */
const FactoryStratifiedScatterChartModel = function(dateTime, stratificationSettings)
{
	return {
		stratificationSettings: stratificationSettings,
		startDateTime:          DateTimeHandlerService.ToUTCDateTime(`${dateTime.startDate} ${dateTime.startTime}`),
		endDateTime:            DateTimeHandlerService.ToUTCDateTime(`${dateTime.endDate} ${dateTime.endTime}`),
	};
};

/**
	Obtém um conjunto de amostras de tendências de valores globais e tendências de frequência para um gráfico básico.
 * @param {object} parameters - Modelo contendo ids das tendências e período de tempo.
 */
const GetScatterChartData = async function(parameters)
{
	const response = await controllers.TrendScatterCharts.GetScatterChartData(parameters);

	if (response.status === 204) return {};

	const data = ScatterChartDataFactory(response.data);

	return data;
};

/**
 * Obtém um conjunto de amostras de tendências de valores globais e tendências de frequência para
 * um gráfico de domínio personalizado em formato de CSV.
 * @param {object} parameters - Parâmetros necessários para a requisição.
 * @returns {object} Arquivo no formato csv para download.
 */
const GetScatterChartCSV = async function(parameters)
{
	const resp = await controllers.TrendScatterCharts.GetScatterChartCSV(parameters);

	if (resp.status === 204) return {};

	const blobFile = new Blob([ resp.data ], { encoding: "UTF-8", type: "text/csv;charset=UTF-8" });
	return blobFile;
};

/**
 * Obtém um conjunto de amostras de tendências de valores globais e tendências de frequência
 * para um gráfico estratificado.
 * @param {object} parameters - Modelo contendo ids das tendências e período de tempo.
 */
const GetStratifiedScatterChartData = async function(parameters)
{
	const response = await controllers.TrendScatterCharts.GetStratifiedScatterChartData(parameters);

	if (response.status === 204) return {};

	const data = ScatterChartDataFactory(response.data);

	return data;
};

/**
 * Obtém um conjunto de amostras de tendências de valores globais e tendências de frequência para
 * um gráfico de estratificações com domínio personalizado em formato de CSV.
 * @param {object} parameters - Parâmetros necessários para a requisição.
 * @returns {object} Arquivo no formato csv para download.
 */
const GetStratifiedScatterChartCSV = async function(parameters)
{
	const resp = await controllers.TrendScatterCharts.GetStratifiedScatterChartCSV(parameters);

	if (resp.status === 204) return {};

	const blobFile = new Blob([ resp.data ], { encoding: "UTF-8", type: "text/csv;charset=UTF-8" });
	return blobFile;
};

/**
 * Função factory que cria um objeto modelo que consta:
 * trends - array com os dados necessários para plot do gráfico de acordo com
 * cada turbina selecionada.
 * alarm - valor de atenção.
 * trip - valor de alerta.
 * @param {object} view - Objeto modelo inicializado.
 * @returns {object} Objeto modelo da configuração de domínio personalizado.
 */
const ScatterChartDataFactory = function(view)
{
	return {
		alarm:  view.alarm,
		trends: view.trends,
		trip:   view.trip
	};
};

export default {
	FactoryEmptyComparativeScatterChartState,
	FactoryScatterChartModel,
	FactoryScatterSettingModel,
	GetScatterChartData,
	FactoryStratifiedScatterChartModel,
	GetStratifiedScatterChartData,
	GetScatterChartCSV,
	GetStratifiedScatterChartCSV,
};